/**
 * @description Time Off tab - PTO balance display and request management
 *
 * Key features:
 * - Display PTO balances (vacation, sick, personal)
 * - List of PTO requests with status badges
 * - Form to submit new PTO requests
 * - Cancel pending requests
 *
 * @dependencies
 * - ClockContext: Session state, company ID
 * - clockService: PTO API calls
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, X, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { useClockContext } from '../../contexts/ClockContext';
import clockService from '../../services/clockService';

// Leave type display configuration
const LEAVE_TYPES = {
  vacation: { label: 'Vacation', color: 'bg-blue-500' },
  sick: { label: 'Sick', color: 'bg-red-500' },
  personal: { label: 'Personal', color: 'bg-purple-500' },
  bereavement: { label: 'Bereavement', color: 'bg-gray-500' },
  jury_duty: { label: 'Jury Duty', color: 'bg-amber-500' },
  other: { label: 'Other', color: 'bg-slate-500' },
};

// Status badge configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', bgColor: 'bg-amber-500/20', textColor: 'text-amber-400' },
  approved: { label: 'Approved', bgColor: 'bg-green-500/20', textColor: 'text-green-400' },
  denied: { label: 'Denied', bgColor: 'bg-red-500/20', textColor: 'text-red-400' },
  cancelled: { label: 'Cancelled', bgColor: 'bg-slate-500/20', textColor: 'text-slate-400' },
  taken: { label: 'Taken', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400' },
};

const TimeOffScreen = () => {
  const { companyId } = useClockContext();
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    leaveType: 'vacation',
    startDate: '',
    endDate: '',
    hoursRequested: '',
    reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Cancel modal state
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [balanceData, requestData] = await Promise.all([
        clockService.getPtoBalances(companyId),
        clockService.getPtoRequests(companyId),
      ]);
      setBalances(balanceData || []);
      setRequests(requestData || []);
    } catch (err) {
      console.error('Error loading PTO data:', err);
      setError('Failed to load PTO data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setFormError(null);

    // Validate
    if (!formData.startDate || !formData.endDate) {
      setFormError('Please select start and end dates');
      return;
    }

    if (!formData.hoursRequested || parseFloat(formData.hoursRequested) <= 0) {
      setFormError('Please enter hours requested');
      return;
    }

    setSubmitting(true);
    try {
      await clockService.submitPtoRequest(companyId, {
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        hoursRequested: parseFloat(formData.hoursRequested),
        reason: formData.reason || null,
      });

      // Reset form and reload data
      setFormData({
        leaveType: 'vacation',
        startDate: '',
        endDate: '',
        hoursRequested: '',
        reason: '',
      });
      setShowRequestForm(false);
      await loadData();
    } catch (err) {
      console.error('Error submitting PTO request:', err);
      setFormError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle request cancellation
  const handleCancel = async () => {
    if (!cancellingRequest) return;

    try {
      await clockService.cancelPtoRequest(companyId, cancellingRequest.id, cancelReason);
      setCancellingRequest(null);
      setCancelReason('');
      await loadData();
    } catch (err) {
      console.error('Error cancelling request:', err);
      setError(err.response?.data?.error || 'Failed to cancel request');
    }
  };

  // Format date for display (simpler format for requests)
  // Handles both ISO timestamps (2026-01-31T00:00:00.000Z) and date-only strings (2026-01-31)
  const formatRequestDate = (dateStr) => {
    if (!dateStr) return '';
    // Extract just the date portion (YYYY-MM-DD) if it's a full timestamp
    const dateOnly = String(dateStr).substring(0, 10);
    const date = new Date(dateOnly + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* PTO Balances Section */}
      <div className="bg-slate-800 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-white mb-4">Your Balances</h2>
        {balances.length === 0 ? (
          <p className="text-slate-400 text-sm">
            No PTO balances configured
            <span className="block mt-1 text-slate-500">
              If your company uses Responsible Time Off, balances are unlimited — requests are still tracked for reporting.
            </span>
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {balances.map((balance) => {
              const typeConfig = LEAVE_TYPES[balance.balanceType] || LEAVE_TYPES.other;
              const available = parseFloat(balance.availableHours || 0);
              const allocated = parseFloat(balance.allocatedHours || 0);
              const pending = parseFloat(balance.pendingHours || 0);

              return (
                <div key={balance.id} className="bg-slate-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${typeConfig.color}`} />
                    <span className="text-sm text-slate-300 capitalize">{typeConfig.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{available}h</p>
                  <p className="text-xs text-slate-500">
                    of {allocated}h
                    {pending > 0 && <span className="text-amber-400"> ({pending}h pending)</span>}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Request Button / Form */}
      {!showRequestForm ? (
        <button
          onClick={() => setShowRequestForm(true)}
          className="w-full flex items-center justify-center gap-2 bg-primary-400 text-slate-900 font-medium py-3 rounded-xl hover:bg-primary-300 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Request Time Off
        </button>
      ) : (
        <div className="bg-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">New Request</h2>
            <button
              onClick={() => setShowRequestForm(false)}
              className="p-1 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {formError && (
            <div className="mb-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-400">{formError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Leave Type */}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Type</label>
              <div className="relative">
                <select
                  value={formData.leaveType}
                  onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                  className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 appearance-none focus:ring-2 focus:ring-primary-400 outline-none"
                >
                  {Object.entries(LEAVE_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  min={formData.startDate}
                  className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary-400 outline-none"
                />
              </div>
            </div>

            {/* Hours Requested */}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Hours Requested</label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={formData.hoursRequested}
                onChange={(e) => setFormData({ ...formData, hoursRequested: e.target.value })}
                placeholder="e.g., 8"
                className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary-400 outline-none placeholder-slate-500"
              />
            </div>

            {/* Reason (Optional) */}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Reason (optional)</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={2}
                placeholder="Any additional details..."
                className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary-400 outline-none placeholder-slate-500 resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary-400 text-slate-900 font-medium py-3 rounded-xl hover:bg-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Submit Request
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white px-1">Your Requests</h2>

        {requests.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No time off requests yet</p>
          </div>
        ) : (
          requests.map((request) => {
            const typeConfig = LEAVE_TYPES[request.leaveType] || LEAVE_TYPES.other;
            const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
            const canCancel = request.status === 'pending' || request.status === 'approved';

            return (
              <div key={request.id} className="bg-slate-800 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full ${typeConfig.color}`} />
                      <span className="text-white font-medium">{typeConfig.label}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bgColor} ${statusConfig.textColor}`}
                      >
                        {statusConfig.label}
                      </span>
                    </div>

                    <p className="text-sm text-slate-400">
                      {formatRequestDate(request.startDate)}
                      {request.startDate !== request.endDate &&
                        ` — ${formatRequestDate(request.endDate)}`}
                    </p>

                    <p className="text-sm text-slate-500 mt-1">
                      {request.hoursRequested}h requested
                    </p>

                    {request.reason && (
                      <p className="text-xs text-slate-500 mt-2 border-t border-slate-700 pt-2">
                        {request.reason}
                      </p>
                    )}

                    {request.reviewNotes && (
                      <p className="text-xs text-slate-400 mt-2 italic">
                        Manager note: {request.reviewNotes}
                      </p>
                    )}
                  </div>

                  {canCancel && (
                    <button
                      onClick={() => setCancellingRequest(request)}
                      className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Cancel request"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {cancellingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-5 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-white mb-2">Cancel Request?</h3>
            <p className="text-sm text-slate-400 mb-4">
              Are you sure you want to cancel this{' '}
              {LEAVE_TYPES[cancellingRequest.leaveType]?.label.toLowerCase() || 'time off'} request?
            </p>

            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why are you cancelling?"
                className="w-full bg-slate-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-400 outline-none placeholder-slate-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCancellingRequest(null);
                  setCancelReason('');
                }}
                className="flex-1 py-2 px-4 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
              >
                Keep It
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeOffScreen;
